/**
 * The Fanar mark system.
 *
 * Albanian road signs follow the Vienna Convention, so drivers already parse three
 * shapes at 100 km/h without reading them: a triangle warns, an octagon stops, a
 * circle informs. The pin borrows that vernacular rather than inventing an icon
 * set, which buys legibility we could never design from scratch.
 *
 * Four independent channels, no colour anywhere:
 *   shape            -> category      (warning / closed / presence)
 *   glyph            -> the hazard    (what is actually on the road)
 *   fill inversion   -> severity      (solid white = rrezik, and only rrezik)
 *   opacity + size   -> freshness     (a stale report visibly dies)
 */
import type { HazardType } from "../../shared/hazards.js";

export type MarkShape = "triangle" | "octagon" | "circle";

/** Most hazards warn. Exactly one closes a road. Two are merely present. */
export const PIN_SHAPE: Record<HazardType, MarkShape> = {
  aksident: "triangle",
  grope: "triangle",
  rreshqitje_dheu: "triangle",
  kafshe_ne_rruge: "triangle",
  permbytje: "triangle",
  punime_pa_ndricim: "triangle",
  tombino_e_hapur: "triangle",
  semafor_i_prishur: "triangle",
  objekt_ne_rruge: "triangle",
  mbyllje_rruge: "octagon",
  mjet_i_ndalur: "circle",
  kontroll_trafiku: "circle",
  kalim_hekurudhor: "triangle",
  rruge_pa_ndricim: "triangle",
  kthese_e_forte: "triangle",
};

/**
 * Glyph artwork on a 48x48 grid, drawn to survive being scaled into a pin.
 * Stroke weight is uniform so that no hazard shouts louder than another —
 * severity is the only thing allowed to do that.
 */
const GLYPH: Record<HazardType, string> = {
  // Two masses driven together, with the seam torn between them. Chevrons and
  // starbursts both read as weather at 28px; solid mass does not.
  aksident: `
    <path d="M3 16 H17 L23 24 L17 32 H3 Z" fill="currentColor" stroke="none"/>
    <path d="M45 16 H31 L25 24 L31 32 H45 Z" fill="currentColor" stroke="none"/>
    <path d="M24 4 L20 12 L28 13 L23 21" stroke-width="3.4"/>
    <path d="M24 44 L28 36 L20 35 L25 27" stroke-width="3.4"/>`,

  // A road surface with a bite taken out of it. One shape, not a profile drawing:
  // at 30px a thin dip line is a "V", but a fat bar with a hole is a pothole.
  grope: `
    <path d="M2 17 H16 A8 8 0 0 0 32 17 H46 V33 H2 Z" fill="currentColor" stroke="none"/>`,

  // The Vienna falling-rocks slope, with the rocks actually on the slope line.
  rreshqitje_dheu: `
    <path d="M4 5 L27 41 H4 Z" fill="currentColor" stroke="none"/>
    <circle cx="33" cy="19" r="4.5" fill="currentColor" stroke="none"/>
    <circle cx="42" cy="31" r="5.5" fill="currentColor" stroke="none"/>
    <circle cx="31" cy="38" r="3.5" fill="currentColor" stroke="none"/>`,

  // Bagëti: a solid four-legged mass with a lowered head. Silhouette, not outline —
  // an outlined animal turns into a comb at pin size.
  kafshe_ne_rruge: `
    <path d="M9 17 H30 L34 12 H43 V19 L37 22 V24 H9 Z" fill="currentColor" stroke="none"/>
    <path d="M11 24 H33 V28 H11 Z" fill="currentColor" stroke="none"/>
    <path d="M13 28 V41 M21 28 V41 M29 28 V41" stroke-width="4.6"/>
    <path d="M9 19 L3 13" stroke-width="3.6"/>`,

  // Water, and nothing else. A depth marker beside the waves read as the letter P;
  // three heavy waves inside a warning triangle on a road can only mean one thing.
  permbytje: `
    <path d="M3 15 Q10 8 17 15 T31 15 T45 15" stroke-width="5.2"/>
    <path d="M3 27 Q10 20 17 27 T31 27 T45 27" stroke-width="5.2"/>
    <path d="M3 39 Q10 32 17 39 T31 39 T45 39" stroke-width="5.2"/>`,

  // A shovel. The barrier-plus-dead-lamp was two ideas fighting for 30px; the
  // works sign's one unmistakable element is the tool. "Unlit" is what the voice
  // says — the glyph only has to say "works".
  punime_pa_ndricim: `
    <path d="M24 3 V26" stroke-width="5.5"/>
    <path d="M17 3 H31" stroke-width="5.5"/>
    <path d="M13 26 H35 L32 44 H16 Z" fill="currentColor" stroke="none"/>`,

  // The cover is off: a disc with a wedge missing, and the wedge lying beside it.
  // Reads as "something is open" even when the disc is 14px across.
  tombino_e_hapur: `
    <path d="M24 6 A18 18 0 1 0 41 30 L24 24 Z" fill="currentColor" stroke="none"/>
    <path d="M33 34 L47 29 L45 44 Z" fill="currentColor" stroke="none"/>`,

  // A solid housing with the lamps punched out of it, then struck through. Three
  // outlined circles in an outlined box are noise at pin size; three holes in a
  // solid pill are a traffic light from across the street.
  semafor_i_prishur: `
    <path d="M15 3 H33 A4 4 0 0 1 37 7 V37 A4 4 0 0 1 33 41 H15 A4 4 0 0 1 11 37 V7 A4 4 0 0 1 15 3 Z
             M24 8 A4.2 4.2 0 1 0 24.01 8 Z M24 18 A4.2 4.2 0 1 0 24.01 18 Z M24 28 A4.2 4.2 0 1 0 24.01 28 Z"
          fill="currentColor" stroke="none" fill-rule="evenodd"/>
    <path d="M6 45 L42 3" stroke-width="5.5"/>`,

  // A vehicle stopped where it should not be.
  mjet_i_ndalur: `
    <path d="M5 30 H8 Q10 19 17 19 H31 Q38 19 40 30 H43 V36 H5 Z" fill="currentColor" stroke="none"/>
    <circle cx="14" cy="38" r="4.5" fill="currentColor" stroke="none"/>
    <circle cx="34" cy="38" r="4.5" fill="currentColor" stroke="none"/>
    <path d="M6 9 H18 M6 15 H14" stroke-width="3.6"/>`,

  // A bar straight across the carriageway. No legs: legs turn it into furniture.
  mbyllje_rruge: `
    <path d="M3 18 H45 V30 H3 Z" fill="currentColor" stroke="none"/>
    <path d="M10 18 L18 30 M22 18 L30 30 M34 18 L42 30" stroke="var(--ground)" stroke-width="3.4"/>`,

  // Debris.
  objekt_ne_rruge: `
    <path d="M12 18 L29 11 L42 24 L32 38 L14 34 Z" fill="currentColor" stroke="none"/>
    <path d="M3 44 H45"/>`,

  // A stop paddle: a disc with a bar through it, on a handle. Deliberately the
  // plainest mark in the set — see plan section 5.
  kontroll_trafiku: `
    <circle cx="24" cy="15" r="10" stroke-width="4.6"/>
    <path d="M17 15 H31" stroke-width="4.2"/>
    <path d="M24 25 V45" stroke-width="5"/>`,

  // The Vienna crossing cross — a saltire.
  kalim_hekurudhor: `
    <path d="M8 8 L40 40 M40 8 L8 40" stroke-width="6"/>`,

  // The Vienna "dangerous bend": a road that turns hard.
  kthese_e_forte: `
    <path d="M14 44 V22 Q14 12 24 12 H30 Q36 12 36 20 V26" stroke-width="5.5" fill="none"/>
    <path d="M28 24 L36 32 L44 24" stroke-width="5" fill="none"/>`,

  // A lamp with nothing coming out of it.
  rruge_pa_ndricim: `
    <path d="M24 6 V16" stroke-width="5"/>
    <path d="M13 16 H35 L31 30 H17 Z" fill="currentColor" stroke="none"/>
    <path d="M24 30 V44" stroke-width="5"/>
    <path d="M6 41 L42 3" stroke-width="4"/>`,
};

const SHAPE_PATH: Record<MarkShape, string> = {
  // Equilateral, apex up, corners softened the way a real sign is.
  triangle: "M32 6 L60 54 Q62 58 57 58 H7 Q2 58 4 54 Z",
  octagon: "M22 4 H42 L60 22 V42 L42 60 H22 L4 42 V22 Z",
  circle: "M32 4 A28 28 0 1 1 31.99 4 Z",
};

/** Where the glyph sits inside each container, as translate + uniform scale. */
const GLYPH_FIT: Record<MarkShape, { x: number; y: number; k: number }> = {
  // A triangle's usable area is low and central, so the glyph drops below centre.
  triangle: { x: 17, y: 22, k: 0.63 },
  octagon: { x: 13, y: 13, k: 0.77 },
  circle: { x: 13.5, y: 13.5, k: 0.78 },
};

const svg = (inner: string, cls = ""): string =>
  `<svg viewBox="0 0 64 64" class="${cls}" fill="none" stroke="currentColor"
        stroke-width="4" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true">${inner}</svg>`;

/**
 * A complete pin: signage container plus hazard glyph.
 * `inverted` is reserved for rrezik and is the strongest signal on the screen.
 */
export function pinMark(type: HazardType, inverted: boolean): string {
  const shape = PIN_SHAPE[type];
  const fit = GLYPH_FIT[shape];
  const container = inverted
    ? `<path d="${SHAPE_PATH[shape]}" fill="currentColor" stroke="currentColor" stroke-width="3"/>`
    : `<path d="${SHAPE_PATH[shape]}" fill="none" stroke="currentColor" stroke-width="5"/>`;
  // When the container is filled, the glyph is knocked out of it.
  const glyphColor = inverted ? 'style="color:var(--ground)"' : "";
  return svg(
    `<circle class="ring" cx="32" cy="32" r="31" pathLength="360" fill="none"
             stroke="currentColor" stroke-width="2" stroke-dasharray="360 360"
             transform="rotate(-90 32 32)" opacity="0"/>
     ${container}
     <g transform="translate(${fit.x} ${fit.y}) scale(${fit.k})" ${glyphColor}
        stroke-width="${(4.6 / fit.k).toFixed(2)}">${GLYPH[type]}</g>`,
  );
}

/**
 * The countdown ring: a hairline arc around the sign that winds down over the
 * report's lifetime, so a stale pin is visibly running out even before it fades.
 * Hidden for the first fifth of life — a fresh report needs no clock on it.
 */
export function setRing(pinEl: HTMLElement, ringDegrees: number, t: number): void {
  const ring = pinEl.querySelector<SVGCircleElement>("circle.ring");
  if (ring === null) return;
  const show = t > 0.2;
  ring.setAttribute("opacity", show ? "0.9" : "0");
  ring.setAttribute("stroke-dasharray", `${Math.max(0, ringDegrees).toFixed(1)} 360`);
}

/** The glyph on its own, for the alert band, the radial menu and the grid,
 *  where the container would only add noise. */
export function glyphMark(type: HazardType): string {
  return `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="4"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${GLYPH[type]}</svg>`;
}

/**
 * The F. The top arm is the widest stroke and the middle arm is inset from it —
 * an F whose middle arm overhangs the top reads as a broken glyph, not a letter.
 * Proportions follow the generated app icon: stem 19 units, arms 19 deep.
 */
export const MARK_F = `<svg viewBox="0 0 100 100" aria-hidden="true">
  <path d="M26 26 H72 V45 H45 V58 H64 V77 H45 V94 H26 Z" fill="currentColor"/></svg>`;

/** Own-vehicle arrow. Never pure white: pins own that. */
export const MARK_VEHICLE = `<svg viewBox="0 0 32 32" aria-hidden="true">
  <path d="M16 3 L27 28 L16 22 L5 28 Z" fill="currentColor"/></svg>`;

/** The report trigger: a beacon throwing light, which is what the user is doing. */
export const MARK_REPORT = `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor"
     stroke-width="4" stroke-linecap="round" aria-hidden="true">
  <path d="M24 10 V4 M11 15 L7 11 M37 15 L41 11"/>
  <path d="M24 16 L36 40 H12 Z" fill="currentColor" stroke="none"/></svg>`;
